import React, { useState } from "react";
import { db } from "../../firebase";
import { collection, doc, getDoc, addDoc, serverTimestamp } from "firebase/firestore";
import useCurrentUser from "../../hooks/useCurrentUser";
import { Plus, Trash2, Search, Save, CheckCircle2, Loader2 } from "lucide-react";

export default function ClientHSEvaluations() {
    const { clientId } = useCurrentUser();
    const [rows, setRows] = useState([
        { id: 1, productName: "", description: "", hsCodeInput: "", value: "", countryOfOrigin: "", result: null }
    ]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const addRow = () => {
        setRows([
            ...rows,
            { id: Date.now(), productName: "", description: "", hsCodeInput: "", value: "", countryOfOrigin: "", result: null }
        ]);
    };

    const removeRow = (id) => {
        if (rows.length > 1) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const handleChange = (id, field, value) => {
        setRows(rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    };

    const checkHSCodes = async () => {
        setLoading(true);
        setSaveSuccess(false);

        const updatedRows = await Promise.all(
            rows.map(async (row) => {
                if (!row.hsCodeInput || !row.value) return row; // Skip empty rows

                try {
                    // Force string formatting to match Super Admin IDs (e.g. "950300" or equivalent)
                    const cleanCode = row.hsCodeInput.replace(/[^0-9]/g, "");
                    const docRef = doc(db, "hsCodes", cleanCode);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        const val = parseFloat(row.value) || 0;
                        const dutyRate = data.dutyRate || 0;
                        const gstRate = data.gstRate || 10;

                        const duty = val * (dutyRate / 100);
                        const gst = (val + duty) * (gstRate / 100);
                        const landedCost = val + duty + gst;

                        return {
                            ...row,
                            result: {
                                found: true,
                                dutyRate,
                                gstRate,
                                duty: parseFloat(duty.toFixed(2)),
                                gst: parseFloat(gst.toFixed(2)),
                                landedCost: parseFloat(landedCost.toFixed(2)),
                                dbDescription: data.description
                            }
                        };
                    } else {
                        return {
                            ...row,
                            result: { found: false, error: "HS Code not found" }
                        };
                    }
                } catch (err) {
                    console.error("Error fetching HS Code:", err);
                    return {
                        ...row,
                        result: { found: false, error: "Lookup error" }
                    };
                }
            })
        );

        setRows(updatedRows);
        setLoading(false);
    };

    const handleSaveEvaluation = async () => {
        setSaving(true);
        try {
            const validRows = rows.filter(r => r.result && r.result.found);

            if (validRows.length === 0) {
                alert("No valid evaluated rows to save.");
                setSaving(false);
                return;
            }

            await addDoc(collection(db, "hsEvaluations"), {
                companyId: clientId,
                userId: clientId,
                rows: validRows.map(r => ({
                    productName: r.productName,
                    description: r.description,
                    hsCode: r.hsCodeInput,
                    value: parseFloat(r.value) || 0,
                    countryOfOrigin: r.countryOfOrigin,
                    dutyRate: r.result.dutyRate,
                    gstRate: r.result.gstRate,
                    customsDuty: r.result.duty,
                    gst: r.result.gst,
                    landedCost: r.result.landedCost
                })),
                createdAt: serverTimestamp()
            });

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000); // clear success msg after 3s
        } catch (err) {
            console.error("Error saving evaluation history:", err);
            alert("Error saving evaluation.");
        }
        setSaving(false);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex items-center justify-between bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-navy-900 font-serif">HS Code Evaluation</h1>
                    <p className="text-slate-500 mt-1">Multi-product instant classification and tariff calculator.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={handleSaveEvaluation}
                        disabled={saving || !rows.some(r => r.result?.found)}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-navy-900 border border-slate-200 font-medium rounded hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : saveSuccess ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Save size={16} />}
                        {saveSuccess ? "Saved!" : "Save Evaluation"}
                    </button>
                    <button
                        onClick={checkHSCodes}
                        disabled={loading}
                        className="flex items-center gap-2 px-6 py-2 bg-gold-500 text-navy-900 font-bold rounded hover:bg-gold-400 transition-colors disabled:opacity-50"
                    >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                        Check HS Codes
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium whitespace-nowrap">
                        <tr>
                            <th className="px-4 py-3 min-w-[150px]">Product Name</th>
                            <th className="px-4 py-3 min-w-[200px]">Description</th>
                            <th className="px-4 py-3 min-w-[120px]">HS Code</th>
                            <th className="px-4 py-3 min-w-[120px]">Value (CIF)</th>
                            <th className="px-4 py-3 min-w-[100px]">Origin</th>
                            <th className="px-4 py-3 min-w-[250px] bg-slate-100/50">Calculated Results</th>
                            <th className="px-4 py-3 w-10 text-center"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.map((row) => (
                            <tr key={row.id} className="hover:bg-slate-50 h-[72px]">
                                <td className="px-4 py-2 align-top">
                                    <input
                                        type="text"
                                        value={row.productName}
                                        onChange={(e) => handleChange(row.id, "productName", e.target.value)}
                                        className="w-full px-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-gold-500"
                                        placeholder="e.g. Toy Car"
                                    />
                                </td>
                                <td className="px-4 py-2 align-top">
                                    <input
                                        type="text"
                                        value={row.description}
                                        onChange={(e) => handleChange(row.id, "description", e.target.value)}
                                        className="w-full px-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-gold-500"
                                        placeholder="e.g. Plastic children's toy"
                                    />
                                </td>
                                <td className="px-4 py-2 align-top">
                                    <input
                                        type="text"
                                        value={row.hsCodeInput}
                                        onChange={(e) => handleChange(row.id, "hsCodeInput", e.target.value)}
                                        className="w-full px-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-gold-500 font-mono font-bold"
                                        placeholder="e.g. 950300"
                                    />
                                </td>
                                <td className="px-4 py-2 align-top">
                                    <div className="relative">
                                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={row.value}
                                            onChange={(e) => handleChange(row.id, "value", e.target.value)}
                                            className="w-full pl-6 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-gold-500"
                                            placeholder="10000"
                                        />
                                    </div>
                                </td>
                                <td className="px-4 py-2 align-top">
                                    <input
                                        type="text"
                                        value={row.countryOfOrigin}
                                        onChange={(e) => handleChange(row.id, "countryOfOrigin", e.target.value)}
                                        className="w-full px-3 py-1.5 text-sm bg-white border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-gold-500"
                                        placeholder="CN"
                                    />
                                </td>
                                <td className="px-4 py-2 align-top border-l border-slate-100 bg-slate-50/50">
                                    {!row.result ? (
                                        <span className="text-slate-400 text-xs italic">Awaiting check...</span>
                                    ) : row.result.found ? (
                                        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">Duty ({row.result.dutyRate}%):</span>
                                                <span className="font-medium">${row.result.duty.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-emerald-700 bg-emerald-50 px-2 rounded col-span-2 row-span-2 py-1 font-bold">
                                                <span>Landed:</span>
                                                <span className="text-sm">${row.result.landedCost.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-500">GST ({row.result.gstRate}%):</span>
                                                <span className="font-medium">${row.result.gst.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-red-500 text-xs font-bold bg-red-50 px-2 py-1 rounded inline-block">
                                            {row.result.error}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-2 align-top text-center border-l border-slate-100">
                                    <button
                                        onClick={() => removeRow(row.id)}
                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="p-4 border-t border-slate-200 bg-slate-50">
                    <button
                        onClick={addRow}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-navy-900 hover:bg-slate-200/70 rounded transition-colors"
                    >
                        <Plus size={16} /> Add Row
                    </button>
                </div>
            </div>
        </div>
    );
}
